import { HttpError } from "@/models/Error"

import { apiService } from "./api.service"
import { Endpoint } from "./endpoints"

export interface StreamTarget {
  accountId: string
  projectId: string
  fileMetadataId: string
}

interface StreamTokenResponse {
  token: string
  /** Seconds. */
  expiresIn: number
}

interface CacheEntry {
  token: string
  expiresAt: number
}

const EXPIRY_MARGIN_MS = 60_000

const DEFAULT_EXPIRES_IN_SECONDS = 1200

function cacheKey({ accountId, projectId, fileMetadataId }: StreamTarget) {
  return `${accountId}|${projectId}|${fileMetadataId}`
}

class StreamTokenService {
  private cache = new Map<string, CacheEntry>()
  private pending = new Map<string, Promise<CacheEntry | HttpError>>()

  async getStreamUrl(target: StreamTarget, { force = false } = {}): Promise<string | HttpError> {
    const token = await this.getToken(target, force)
    if (token instanceof HttpError) return token

    return Endpoint.ATTACHMENT_VIDEO_URL(
      target.accountId,
      target.projectId,
      target.fileMetadataId,
      token
    ).externalUrl
  }

  private async getToken(target: StreamTarget, force: boolean): Promise<string | HttpError> {
    const key = cacheKey(target)

    if (force) {
      this.cache.delete(key)
      this.pending.delete(key)
    }

    const cached = this.cache.get(key)
    if (cached && cached.expiresAt - EXPIRY_MARGIN_MS > Date.now()) return cached.token

    // Several components can open the same attachment at once; they share one mint request.
    let request = this.pending.get(key)
    if (!request) {
      request = this.mint(target)
      this.pending.set(key, request)
      request.finally(() => {
        if (this.pending.get(key) === request) this.pending.delete(key)
      })
    }

    const entry = await request
    if (entry instanceof HttpError) return entry

    this.cache.set(key, entry)
    return entry.token
  }

  private async mint(target: StreamTarget): Promise<CacheEntry | HttpError> {
    const data = await apiService.post<StreamTokenResponse>(
      Endpoint.ATTACHMENT_STREAM_TOKEN(target.accountId, target.projectId, target.fileMetadataId)
    )

    if (data instanceof HttpError) return data
    if (!data?.token) return new HttpError({ message: "Stream token response contains no token" })

    const expiresIn = Number(data.expiresIn)
    const lifetime = expiresIn > 0 ? expiresIn : DEFAULT_EXPIRES_IN_SECONDS

    return { token: data.token, expiresAt: Date.now() + lifetime * 1000 }
  }
}

export const streamTokenService = new StreamTokenService()
